import { useLayoutEffect } from "react";

import { type OnOptions, on } from "~/tools/on";
import { useHandler } from "~/tools/use-handler";

export interface UseOnOptions<TNode> extends Omit<OnOptions<TNode>, "node"> {
	/**
	 * @default document
	 */
	node?: null | undefined | TNode;

	/**
	 * @default true
	 */
	enabled?: boolean;
}

export function useOn<TEventName extends keyof DocumentEventMap, TNode extends Element | Document | Window = Document>(
	eventName: TEventName,
	handler: (element: TNode, event: DocumentEventMap[TEventName]) => void,
	{ node, enabled = true, throttle, passive, capture }: UseOnOptions<TNode> = {},
): void {
	const stableHandler = useHandler(handler);
	const enabledNode = enabled ? (node ?? (document as TNode)) : null;

	useLayoutEffect(() => {
		if (enabledNode == null) {
			return;
		}

		return on(eventName, stableHandler, { node: enabledNode, throttle, passive, capture });
	}, [eventName, stableHandler, enabledNode, throttle, passive, capture]);
}
